// src/components/AdminView.js
import { useState } from "react";
import { useProjects, usePhotoUpload, deletePhoto, deleteProject, moveProjectType } from "../hooks/useFirebase";
import { theme, metalPanelStyle, accentBar } from "../theme";

function ProjectCard({ typeId, project, types }) {
  const { uploadPhotos, uploading, progress } = usePhotoUpload();
  const [deleting, setDeleting] = useState(false);
  const [moveTo, setMoveTo] = useState("");
  const [moving, setMoving] = useState(false);

  const otherTypes = types.filter((t) => t.id !== typeId);

  const handleFiles = async (e) => {
    if (!e.target.files.length) return;
    await uploadPhotos(typeId, project.id, e.target.files, project.photos || []);
    e.target.value = "";
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm("Remove this photo?")) return;
    await deletePhoto(typeId, project.id, photo, project.photos || []);
  };

  const handleDeleteProject = async () => {
    if (!window.confirm(`Delete project "${project.name}" and all its photos?`)) return;
    setDeleting(true);
    await deleteProject(typeId, project.id, project.photos || []);
  };

  const handleMove = async () => {
    if (!moveTo) return;
    setMoving(true);
    try {
      await moveProjectType(typeId, moveTo, project.id, project);
    } finally {
      setMoving(false);
      setMoveTo("");
    }
  };

  return (
    <div style={{ ...metalPanelStyle({ padding: "1.25rem", marginBottom: "12px" }), display: "flex", gap: "12px" }}>
      <div style={{ ...accentBar, alignSelf: "stretch" }} aria-hidden />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "12px", flexWrap: "wrap", gap: "10px" }}>
          <div>
            <div style={{ fontWeight: 600, color: theme.text, fontSize: "15px" }}>{project.name}</div>
            <div style={{ color: theme.textMuted, fontSize: "12px", marginTop: "3px" }}>
              {project.year}{project.desc ? ` — ${project.desc}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
            <span style={{ fontSize: "11px", color: theme.textMuted, fontFamily: theme.fontSansNarrow, letterSpacing: "0.06em" }}>
              {(project.photos || []).length} photo{(project.photos || []).length !== 1 ? "s" : ""}
            </span>
            <label style={styles.uploadLabel}>
              {uploading ? "Uploading…" : "Upload Photos"}
              <input type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFiles} disabled={uploading} />
            </label>
            {otherTypes.length > 0 && (
              <>
                <select
                  value={moveTo}
                  onChange={(e) => setMoveTo(e.target.value)}
                  disabled={moving}
                  style={{ ...styles.input, width: "auto", padding: "6px 8px", fontSize: "12px" }}
                >
                  <option value="">Move to…</option>
                  {otherTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
                  ))}
                </select>
                <button type="button" onClick={handleMove} disabled={moving || !moveTo} style={styles.uploadLabel}>
                  {moving ? "Moving…" : "Move"}
                </button>
              </>
            )}
            <button type="button" onClick={handleDeleteProject} disabled={deleting} style={styles.deleteBtn}>
              {deleting ? "…" : "Delete"}
            </button>
          </div>
        </div>

        {Object.entries(progress).map(([name, pct]) => (
          <div key={name} style={{ marginBottom: "6px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: theme.textMuted, marginBottom: "3px" }}>
              <span>{name}</span><span>{pct}%</span>
            </div>
            <div style={{ background: theme.bgDeep, borderRadius: "0px", height: "4px", border: `1px solid ${theme.border}` }}>
              <div style={{ background: `linear-gradient(90deg, ${theme.brand} 0%, ${theme.brandDark} 100%)`, width: `${pct}%`, height: "100%", borderRadius: "0px", transition: "width .2s" }} />
            </div>
          </div>
        ))}

        {(project.photos || []).length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "8px", marginTop: "10px" }}>
            {(project.photos || []).map((ph, i) => (
              <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: theme.radiusTight, overflow: "hidden", background: theme.surfaceMuted, border: `1px solid ${theme.border}` }}>
                <img src={ph.url} alt={ph.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <button
                  type="button"
                  onClick={() => handleDeletePhoto(ph)}
                  style={{ position: "absolute", top: "3px", right: "3px", background: "rgba(0,0,0,.78)", border: "none", color: "#fff", borderRadius: "0px", width: "22px", height: "22px", fontSize: "14px", lineHeight: "22px", textAlign: "center", cursor: "pointer" }}
                >×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminView({ types, onAddType, onDeleteType }) {
  const [activeType, setActiveType] = useState(types[0]?.id || null);
  const [newTypeName, setNewTypeName] = useState("");
  const [newProj, setNewProj] = useState({ name: "", year: new Date().getFullYear() + "", desc: "" });

  const curType = types.find((t) => t.id === activeType);
  const { projects, addProject } = useProjects(activeType);

  const handleAddType = async () => {
    if (!newTypeName.trim()) return;
    const ref = await onAddType(newTypeName.trim());
    setActiveType(ref?.id || null);
    setNewTypeName("");
  };

  const handleAddProject = async () => {
    if (!newProj.name.trim() || !activeType) return;
    await addProject(activeType, newProj);
    setNewProj({ name: "", year: new Date().getFullYear() + "", desc: "" });
  };

  return (
    <main style={{ maxWidth: theme.maxContent, margin: "0 auto", padding: "clamp(1.25rem, 3vw, 2rem)" }}>
      <header style={{ ...metalPanelStyle({ padding: "1.25rem 1.5rem", marginBottom: "1.5rem" }), display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <div style={{ ...accentBar, height: "40px" }} aria-hidden />
          <div>
            <p style={styles.eyebrow}>Operations</p>
            <h2 style={styles.title}>Portfolio CMS</h2>
          </div>
        </div>
        <span style={styles.badge}>Staff access</span>
      </header>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", alignItems: "flex-start" }}>
        <aside style={{ ...metalPanelStyle({ padding: "1rem", flex: "1 1 240px", maxWidth: "100%" }) }}>
          <p style={styles.sectionLabel}>Construction types</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "14px" }}>
            {types.map((t) => (
              <div
                key={t.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "10px 12px",
                  borderRadius: theme.radiusTight,
                  background: activeType === t.id ? theme.brandMuted : "transparent",
                  border: `1px solid ${activeType === t.id ? theme.borderStrong : "transparent"}`,
                  borderLeft: activeType === t.id ? `3px solid ${theme.brand}` : "3px solid transparent",
                  cursor: "pointer",
                }}
                onClick={() => setActiveType(t.id)}
                onKeyDown={(e) => e.key === "Enter" && setActiveType(t.id)}
                role="button"
                tabIndex={0}
              >
                <span style={{ flex: 1, fontSize: "13px", fontWeight: activeType === t.id ? 600 : 500, color: activeType === t.id ? theme.brandDark : theme.textMuted }}>{t.icon} {t.name}</span>
                <span style={styles.countBadge}>{projects.length && activeType === t.id ? projects.length : "·"}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDeleteType(t.id); }}
                  style={{ background: "none", border: "none", color: theme.textMuted, fontSize: "16px", cursor: "pointer", lineHeight: 1 }}
                >×</button>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddType()}
              placeholder="New type…"
              style={styles.input}
            />
            <button type="button" onClick={handleAddType} style={styles.primaryBtn}>+</button>
          </div>
        </aside>

        <section style={{ flex: "1 1 400px", minWidth: 0 }}>
          {curType ? (
            <>
              <div style={{ ...metalPanelStyle({ padding: "1.5rem", marginBottom: "1rem" }) }}>
                <p style={styles.sectionLabel}>New project — {curType.name}</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: "10px", marginBottom: "10px" }}>
                  <input
                    value={newProj.name}
                    onChange={(e) => setNewProj((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Project name"
                    style={styles.input}
                  />
                  <input
                    value={newProj.year}
                    onChange={(e) => setNewProj((p) => ({ ...p, year: e.target.value }))}
                    placeholder="Year"
                    style={styles.input}
                  />
                </div>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <input
                    value={newProj.desc}
                    onChange={(e) => setNewProj((p) => ({ ...p, desc: e.target.value }))}
                    placeholder="Short description (optional)"
                    style={{ ...styles.input, flex: "1 1 200px" }}
                  />
                  <button type="button" onClick={handleAddProject} style={{ ...styles.primaryBtn, padding: "8px 20px", whiteSpace: "nowrap" }}>
                    Add project
                  </button>
                </div>
              </div>

              {projects.length === 0 && (
                <p style={{ color: theme.textMuted, fontSize: "13px", fontStyle: "italic", marginBottom: "8px" }}>No projects yet.</p>
              )}
              {projects.map((p) => (
                <ProjectCard key={p.id} typeId={activeType} project={p} types={types} />
              ))}
            </>
          ) : (
            <div style={{ ...metalPanelStyle({ padding: "2rem" }) }}>
              <p style={{ color: theme.textMuted, fontSize: "14px" }}>Select or create a construction type.</p>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

const styles = {
  eyebrow: {
    color: theme.brand,
    fontSize: "11px",
    fontFamily: theme.fontSansNarrow,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    marginBottom: "4px",
    fontWeight: 600,
  },
  title: { fontFamily: theme.fontSans, fontSize: "22px", fontWeight: 700, color: theme.text, letterSpacing: "-0.02em" },
  badge: {
    background: theme.brandMuted,
    border: `1px solid ${theme.brand}`,
    color: theme.brandDark,
    fontSize: "11px",
    fontFamily: theme.fontSansNarrow,
    fontWeight: 600,
    padding: "6px 14px",
    borderRadius: theme.radiusTight,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  sectionLabel: {
    fontSize: "11px",
    fontFamily: theme.fontSansNarrow,
    letterSpacing: "0.14em",
    color: theme.textMuted,
    textTransform: "uppercase",
    marginBottom: "10px",
    fontWeight: 600,
  },
  countBadge: { fontSize: "11px", color: theme.textMuted, background: theme.bg, borderRadius: "0px", padding: "2px 8px", border: `1px solid ${theme.border}` },
  input: {
    background: theme.surface,
    border: `1px solid ${theme.border}`,
    color: theme.text,
    borderRadius: theme.radiusTight,
    padding: "8px 12px",
    fontSize: "13px",
    outline: "none",
    fontFamily: theme.fontSans,
    width: "100%",
  },
  primaryBtn: {
    background: `linear-gradient(180deg, ${theme.brand} 0%, ${theme.brandDark} 100%)`,
    border: "none",
    color: "#fff",
    borderRadius: theme.radiusTight,
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: theme.fontSans,
    boxShadow: `0 2px 6px rgba(0,0,255,0.2)`,
  },
  uploadLabel: {
    background: `linear-gradient(180deg, #fff 0%, ${theme.surfaceMuted} 100%)`,
    border: `1px solid ${theme.border}`,
    color: theme.text,
    borderRadius: theme.radiusTight,
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxShadow: theme.shadowInset,
  },
  deleteBtn: {
    background: "transparent",
    border: `1px solid ${theme.dangerBorder}`,
    color: theme.danger,
    borderRadius: theme.radiusTight,
    padding: "6px 10px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 600,
  },
};
